import React, { useCallback, useState } from 'react';
import { IconCheck, IconPlus, IconX } from '@tabler/icons-react';
import { UncontrolledTooltip } from 'reactstrap';
import { NSButton, NSInput } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import useToast from 'hooks/useToast';
import { useCreateComment } from '../../hooks/useCreateComment';
import { useAuthContext } from 'contexts/AuthContext';
import { htmlToPlainText } from '../../helpers';
import invariant from 'helpers/invariant';
import './EditableDisplayComment.scss';

interface IEditableDisplayCommentProps {
    timelineId: string;
    dealId: number;
    latestComment?: string;
    className?: string;
    isHovered?: boolean;
    handleCommentClick?: () => void;
}

const EditableDisplayComment: React.FC<IEditableDisplayCommentProps> = ({
    timelineId,
    dealId,
    latestComment = '',
    className = '',
    isHovered = false,
    handleCommentClick = () => {},
}) => {
    const { selectedOrganizationId } = useAuthContext();
    invariant(selectedOrganizationId, 'selectedOrganizationId is required');

    const [isEditing, setIsEditing] = useState(false);
    const [draftComment, setDraftComment] = useState('');
    const { showError, showSuccess } = useToast();
    const { mutateAsync: createComment, isPending } = useCreateComment();
    
    const focusRef = useCallback((node: HTMLInputElement | null) => { node?.focus(); }, []);
    const plainTextComment = latestComment ? htmlToPlainText(latestComment) : '';
    
    const cancelEditing = () => {
        setIsEditing(false);
        setDraftComment('');
    };

    const commitComment = async () => {
        const trimmedComment = draftComment.trim();
        
        if (!trimmedComment) {
            return;
        }

        try {
            await createComment({
                orgId: selectedOrganizationId,
                dealId,
                timelineId: Number(timelineId),
                payload: {
                    bodyHtml: trimmedComment,
                    parentId: null,
                },
            });
            
            showSuccess('Comment added');
            setDraftComment('');
            setIsEditing(false);
        } catch {
            showError('Failed to add comment. Please try again.');
        }
    };

    const handleKeyDown = async (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commitComment();
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            setIsEditing(false);
            setDraftComment('');
        }
    };

    const handleBlur = () => {
        setIsEditing(false);
        setDraftComment('');
    }

    return (
        <div 
            className={`d-flex align-items-center ${className}`}
        >
            <RenderIf isTrue={!isEditing}>
                <RenderIf isTrue={!!plainTextComment}>
                    <div 
                        id={`comment-${timelineId}`}
                        className="text-truncate"
                        onClick={handleCommentClick}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleCommentClick();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        {plainTextComment}
                    </div>
                    <UncontrolledTooltip 
                        target={`comment-${timelineId}`}
                        placement="top"
                        className="EditableDisplayComment__tooltip"
                    >
                        {plainTextComment}
                    </UncontrolledTooltip>
                </RenderIf>
                <div className={`text-nowrap ml-auto ${isHovered ? '' : 'invisible'}`}>
                    <NSButton
                        className="font-weight-normal"
                        callback={() => setIsEditing(true)}
                        ariaLabel="Add comment"
                        icon={IconPlus}
                    >
                        Add comment
                    </NSButton>
                </div>
            </RenderIf>
            <RenderIf isTrue={isEditing}>
                <NSInput
                    name={`editableDisplayComment-${timelineId}`}
                    type="text"
                    value={draftComment}
                    innerRef={focusRef}
                    placeholder="Enter comment..."
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraftComment(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    disabled={isPending}
                />
                <NSButton
                    icon={IconCheck}
                    size="sm"
                    className="px-2 ml-1 text-muted"
                    callback={commitComment}
                    ariaLabel="Save comment"
                    color='light'
                    disabled={isPending}
                    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                />
                <NSButton
                    icon={IconX}
                    size="sm"
                    className="px-2 ml-1 text-muted"
                    color='light'
                    callback={cancelEditing}
                    ariaLabel="Cancel"
                    disabled={isPending}
                    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                />
            </RenderIf>
        </div>
    );
};

export default EditableDisplayComment;
